import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import {
  Checkbox,
  MaskedInput,
  MultiSelect,
  Select,
  TextInput,
} from "components/inputs";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { setShowResolveCMSErrorModal } from "reducers/claims";
import { setValidationState } from "reducers/main";
import {
  useGetClaimByIdQuery,
  useGetClaimValidationsQuery,
  usePatchClaimByIdMutation,
} from "services/claims-api";
import { setCurrentClaim } from "services/claimsDataSlice.ts";
import { useGetTagsByRreIdQuery } from "services/rre-api";
import { useToken } from "utils/context";
import { useDispatch, useSelector } from "utils/redux";

import {
  ControlledDateInput,
  ControlledDateOfBirthInput,
  ControlledInput,
} from "../../components/Card/ControlledInput";
import ClaimsPageContainer from "./ClaimsPageContainer";
import ClaimsTPOCCard from "./ClaimsTPOCCard";

const ORMInformation = ({
  constants,
  control,
  errors,
  focusedField,
  handleSave,
}) => {
  return (
    <Card heading="Ongoing Responsibility for Medicals" cardNumber={2}>
      <Controller
        control={control}
        name={"orm_indicator"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="ORM"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.TRUE_FALSE_CHOICES || []}
            value={constants?.TRUE_FALSE_CHOICES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("orm_indicator", o.value);
            }}
          />
        )}
      />
      <ControlledDateInput
        control={control}
        allowFutureDates={true}
        label={"ORM Termination Date"}
        name={"orm_termination_date"}
        errors={errors}
        onSave={(value) => handleSave("orm_termination_date", value)}
      />
    </Card>
  );
};

const NextActionOverrides = ({
  constants,
  control,
  focusedField,
  handleSave,
  getValues,
}) => {
  const discontinueQueryLogicSetting = getValues(
    "discontinue_query_logic_setting",
  );
  return (
    <Card heading="Next Action Overrides" cardNumber={3}>
      <Controller
        key={"is_held"}
        name={"is_held"}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Hold Claim"
            name={name}
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("is_held", e.target.checked);
            }}
          />
        )}
      />
      <Controller
        key={"should_force_report"}
        name={"should_force_report"}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Force Report"
            name={name}
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("should_force_report", e.target.checked);
            }}
          />
        )}
      />
      <Controller
        control={control}
        key={"discontinue_query_logic_setting"}
        name={"discontinue_query_logic_setting"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="Discontinue Query Configuration"
            name={name}
            isFocused={focusedField === name}
            options={constants?.DISCONTINUE_QUERY_LOGIC_SETTINGS || []}
            value={constants?.DISCONTINUE_QUERY_LOGIC_SETTINGS?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("discontinue_query_logic_setting", o.value);
            }}
          />
        )}
      />
      <Controller
        key={"discontinue_query"}
        name={"discontinue_query"}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Discontinue Query"
            name={name}
            checked={value}
            readOnly={discontinueQueryLogicSetting === "A"}
            disabledReason={
              discontinueQueryLogicSetting === "A"
                ? "System is selected to calculate discontinue query flag."
                : null
            }
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("discontinue_query", e.target.checked);
            }}
          />
        )}
      />
    </Card>
  );
};

const RemoveFromCMS = ({ control, focusedField, handleSave }) => {
  return (
    <Card heading="Remove Claim From CMS" cardNumber={4}>
      <Controller
        key={"marked_for_cms_deletion"}
        name={"marked_for_cms_deletion"}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Remove Claim"
            name={name}
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("marked_for_cms_deletion", e.target.checked);
            }}
          />
        )}
      />
    </Card>
  );
};

const ClaimInformation = ({
  constants,
  tagOptions,
  control,
  getValues,
  errors,
  focusedField,
  handleSave,
}) => {
  const isMedicareBeneficiary = getValues("is_medicare_beneficiary");
  return (
    <Card heading="General Information" cardNumber={1}>
      <ControlledInput
        control={control}
        name="adjuster"
        label="Adjuster ID"
        errors={errors}
        onSave={(value) => handleSave("adjuster", value)}
        componentType={TextInput}
      />
      <Controller
        control={control}
        name={"tags"}
        render={({ field: { name, value, onChange } }) => (
          <MultiSelect
            label="Tags"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={tagOptions || []}
            value={value}
            onChange={(options: OptionType[]) => {
              onChange(options);
              handleSave(
                "tag_ids",
                options.map((option) => option.value),
              );
            }}
          />
        )}
      />
      <ControlledInput
        control={control}
        name="icn"
        label="ICN"
        errors={errors}
        onSave={(value) => handleSave("icn", value)}
        componentType={TextInput}
        maxLength={30}
      />
      <ControlledInput
        control={control}
        name="claim_number"
        label="Claim Number"
        errors={errors}
        onSave={(value) => handleSave("claim_number", value)}
        componentType={TextInput}
        maxLength={30}
      />
      <ControlledInput
        control={control}
        name="injured_party_first_name"
        label="First Name"
        errors={errors}
        readOnly={isMedicareBeneficiary === true}
        onSave={(value) => handleSave("injured_party_first_name", value)}
        componentType={TextInput}
        maxLength={30}
      />
      <ControlledInput
        control={control}
        name="injured_party_middle_initial"
        label="Middle Initial"
        errors={errors}
        readOnly={isMedicareBeneficiary === true}
        onSave={(value) => handleSave("injured_party_middle_initial", value)}
        componentType={TextInput}
        maxLength={1}
      />
      <ControlledInput
        control={control}
        name="injured_party_last_name"
        label="Last Name"
        errors={errors}
        readOnly={isMedicareBeneficiary === true}
        onSave={(value) => handleSave("injured_party_last_name", value)}
        componentType={TextInput}
        maxLength={40}
      />
      <Controller
        control={control}
        name={"injured_party_gender"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="Gender"
            name={name}
            errors={errors}
            readOnly={isMedicareBeneficiary === true}
            isFocused={focusedField === name}
            options={constants?.GENDERS || []}
            value={constants?.GENDERS?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("injured_party_gender", o.value);
            }}
          />
        )}
      />
      <ControlledDateOfBirthInput
        control={control}
        allowFutureDates={false}
        readOnly={isMedicareBeneficiary === true}
        label={"Birth Date"}
        name={"injured_party_dob"}
        errors={errors}
        onSave={(value) => handleSave("injured_party_dob", value)}
      />
      <ControlledInput
        control={control}
        name="injured_party_ssn"
        label="SSN"
        errors={errors}
        readOnly={isMedicareBeneficiary === true}
        onSave={(value) => handleSave("injured_party_ssn", value)}
        componentType={MaskedInput}
        mask="ssn"
        maxLength={11}
      />
      <ControlledInput
        control={control}
        name="injured_party_hicn"
        label="Medicare ID"
        errors={errors}
        readOnly={isMedicareBeneficiary === true}
        onSave={(value) => handleSave("injured_party_hicn", value)}
        componentType={MaskedInput}
        mask="medicare"
        maxLength={12}
      />
      <Controller
        control={control}
        name={"is_medicare_beneficiary"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="Is Medicare Beneficiary"
            name={name}
            errors={errors}
            readOnly={
              getValues("is_medicare_entitlement_date_sourced_from_cms") ===
              true
            }
            isFocused={focusedField === name}
            options={constants?.BENEFICIARY_STATUS || []}
            value={constants?.BENEFICIARY_STATUS?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("is_medicare_beneficiary", o.value);
            }}
          />
        )}
      />

      <ControlledDateInput
        control={control}
        allowFutureDates={true}
        label={"Medicare Effective Date"}
        name={"medicare_entitlement_date"}
        errors={errors}
        onSave={(value) => handleSave("medicare_entitlement_date", value)}
      />
      <Controller
        key={"is_deceased"}
        name="is_deceased"
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Is Deceased"
            name="is_deceased"
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("is_deceased", e.target.checked);
            }}
          />
        )}
      />
      <ControlledDateInput
        control={control}
        allowFutureDates={false}
        label={"Deceased Date"}
        name={"deceased_on_date"}
        errors={errors}
        onSave={(value) => handleSave("deceased_on_date", value)}
      />
      <Controller
        control={control}
        name={"state_of_venue"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="State of Venue"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.US_STATES_OR_FC_OR_US_CHOICES || []}
            value={constants?.US_STATES_OR_FC_OR_US_CHOICES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("state_of_venue", o.value);
            }}
          />
        )}
      />
      <ControlledDateInput
        control={control}
        label={"CMS Date of Incident"}
        name={"cms_date_of_incident"}
        errors={errors}
        allowFutureDates={false}
        onSave={(value) => handleSave("cms_date_of_incident", value)}
      />
      <Controller
        key={"exposure_continued_post_1980"}
        name="exposure_continued_post_1980"
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Exposure continued post 1980"
            name="exposure_continued_post_1980"
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("exposure_continued_post_1980", e.target.checked);
            }}
          />
        )}
      />
      <ControlledDateInput
        control={control}
        label={"Industry Date of Incident"}
        name={"industry_date_of_incident"}
        errors={errors}
        allowFutureDates={false}
        onSave={(value) => handleSave("industry_date_of_incident", value)}
      />
    </Card>
  );
};

const ClaimOverview = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const { accessToken } = useToken();
  const dispatch = useDispatch();
  const [patchClaimById] = usePatchClaimByIdMutation();
  const [hasShownResolveCMSErrorsOnLoad, setHasShownResolveCMSErrorsOnLoad] =
    useState<boolean>(false);

  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm(); // Default for claimant selector);

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  const rreId = claim && claim.rre.toString();

  const { data: tagData } = useGetTagsByRreIdQuery({ rreId }, { skip: !rreId });
  const tagOptions = tagData?.map((tag) => ({
    label: tag.name,
    value: tag.id,
  }));
  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
      reset(claim);
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const fieldsPotentiallyAffectedByUpdate = ["discontinue_query"];

  useEffect(() => {
    if (!hasShownResolveCMSErrorsOnLoad && claim?.has_cms_errors_to_resolve) {
      const timer = setTimeout(() => {
        dispatch(setShowResolveCMSErrorModal(true));
        setHasShownResolveCMSErrorsOnLoad(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [
    isLoading,
    claim,
    hasShownResolveCMSErrorsOnLoad,
    setHasShownResolveCMSErrorsOnLoad,
  ]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchClaimById({
          id: claimId,
          data: field === "" ? value : { [field]: value },
          useDetail: true,
        })
          .unwrap()
          .then((result) => {
            dispatch(setCurrentClaim(result));
            fieldsPotentiallyAffectedByUpdate.map((field) => {
              setValue(field, result[field]);
            });
          });
      } catch (error) {
        console.error("Save failed:", error);
        KaboodleToaster.error(error);
      }
    },
    [patchClaimById, accessToken, claimId, dispatch, setValue],
  );

  const constants = useSelector((state) => state.shared.constants);

  const focusedField = useSelector((state) => state.main.focusedField);

  return (
    <ClaimsPageContainer>
      <div key={`claim-overview-form`}>
        <div className="grid-layout gl-2 no-gap-tb">
          <div>
            <ClaimInformation
              constants={constants}
              tagOptions={tagOptions}
              control={control}
              getValues={getValues}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
            />
          </div>
          <div>
            <ORMInformation
              constants={constants}
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
            />
            <ClaimsTPOCCard claim={claim} onSave={handleSave} />
            <NextActionOverrides
              constants={constants}
              control={control}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
            />
            <RemoveFromCMS
              control={control}
              focusedField={focusedField}
              handleSave={handleSave}
            />
          </div>
        </div>
      </div>
    </ClaimsPageContainer>
  );
};

export default ClaimOverview;
